import React, { FC, useEffect } from 'react';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useRouteUrl } from '../../hooks/use-route-url';
import styled from 'styled-components';
import OvButton from '../UI/atoms/OvButton';
import { useTranslation } from 'react-i18next';
import Variables from '../../design-system/variables';
import Colours from '../../design-system/colours';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAppSelector } from '../../redux/hooks';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import { last } from 'lodash';
import OvDateParser from '../UI/atoms/OvDateParser';
import OvJournalEntryDetailPanel from '../UI/organisms/OvJournalEntryDetailPanel';
import OvUserScanList from '../UI/organisms/OvUserScanList';
import OvPrettyUserName from '../UI/atoms/OvPrettyUserName';
import { ClinicLocation } from '../../common/model/dto/clinic-location';
import { useQuery } from '@tanstack/react-query';
import queryString from 'query-string';
import { ClinicLocationService } from '../../services/clinic-location.service';
import { useClinicLocationPatient } from '../../hooks/use-clinic-location-patient';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import { getAxiosErrorMessage } from '../../common/utils/helpers';
import { Alert, AlertTitle, Tab, Tabs } from '@mui/material';
import OvWearableInfo from '../UI/organisms/OvWearableInfo';

const PATIENT_DAILY_DATA_SCAN_LIMIT = 100;

const PatientDailyDataDetails: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const url = useRouteUrl();
  let location = useLocation();

  const { dailyDataId }: any = useParams();
  const tabs = {
    JOURNAL_ENTRY: 'journal-entry',
    SCANS: 'scans',
    WEARABLE_INFO: 'wearable-info',
  };
  const [tab, setTab] = React.useState<string>(tabs.JOURNAL_ENTRY);
  const [prevPath, setPrevPath] = React.useState<string>('');
  const selectedClinicLocation: ClinicLocation | undefined = useAppSelector(
    (state) => state.user.selectedClinicLocation
  );
  const clinicLocationId = selectedClinicLocation?.id;
  const [patientId, day]: (string | undefined)[] =
    dailyDataId?.split('_') ?? [];
  const isEnabled = !!clinicLocationId && !!patientId && !!day;

  const {
    data: selectedDailyData,
    isLoading: isDailyDataLoading,
    error: dailyDataError,
  } = useQuery({
    queryKey: [
      'clinic-location-patient-daily-data',
      clinicLocationId,
      patientId,
      day,
    ],
    queryFn: async () => {
      const paginatedDailyData =
        await ClinicLocationService.getPatientDailyData(
          clinicLocationId!,
          patientId!,
          queryString.stringify({
            limit: 1,
            user_document_id: patientId,
            day_from: day,
            day_to: day,
          })
        );

      return paginatedDailyData.docs[0] ?? null;
    },
    enabled: isEnabled,
  });

  const { data: scanList = [], error: scansError } = useQuery({
    queryKey: [
      'clinic-location-patient-scans',
      clinicLocationId,
      patientId,
      day,
    ],
    queryFn: async () => {
      const paginatedScans = await ClinicLocationService.getPatientScans(
        patientId!,
        clinicLocationId,
        queryString.stringify({
          limit: PATIENT_DAILY_DATA_SCAN_LIMIT,
          day_from: day,
          day_to: day,
        })
      );

      return paginatedScans.docs;
    },
    enabled: isEnabled,
  });

  const {
    data: selectedPatient,
    isLoading: isPatientLoading,
    error: patientError,
  } = useClinicLocationPatient(clinicLocationId, patientId);

  const isLoading = isDailyDataLoading || isPatientLoading;
  const error = dailyDataError ?? scansError ?? patientError;

  useEffect(() => {
    const setActiveTabFromUrl = () => {
      const pathName = last(location.pathname.split('/'));

      if (
        pathName === tabs.SCANS ||
        pathName === tabs.JOURNAL_ENTRY ||
        pathName === tabs.WEARABLE_INFO
      ) {
        setTab(pathName);
      }
    };

    setActiveTabFromUrl();
  }, [
    tabs.SCANS,
    tabs.JOURNAL_ENTRY,
    tabs.WEARABLE_INFO,
    navigate,
    url,
    location.pathname,
  ]);

  useEffect(() => {
    if ((location as any).state?.prevPath) {
      setPrevPath((location as any)?.state?.prevPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onBackClicked = () => {
    if (prevPath) {
      navigate(prevPath);
    } else {
      navigate(-1);
    }
  };

  const handleTabChange = (event: any, newTab: string) => {
    if (newTab !== null) {
      setTab(newTab);
    }
  };

  return (
    <>
      <DetailPageWrapper>
        <Header>
          <BackCta onClick={onBackClicked}>
            <StyledArrowBackIcon />
            {t('common.actions.back')}
          </BackCta>
          <StyledLink to={`/users/${selectedDailyData?.user_id}`}>
            <PrettyUserName>
              <OvPrettyUserName user={selectedPatient} />
            </PrettyUserName>
          </StyledLink>
          <DateInfo>
            <Separator>-</Separator>
            {selectedDailyData && (
              <OvDateParser
                date={new Date(selectedDailyData.day as string)}
                format={'dddd, MMMM DD, YYYY'}
              />
            )}
          </DateInfo>
        </Header>

        {error && <OvErrorMessage message={getAxiosErrorMessage(error)} />}

        {selectedDailyData?.journal_entry?.is_demo_journal_entry && (
          <StyledAlert severity="warning">
            <AlertTitle>
              {t('dailyDataDetails.journalEntry.demoAlertTitle')}
            </AlertTitle>
            {t('dailyDataDetails.journalEntry.demoAlertDesc')}
          </StyledAlert>
        )}

        <TabContainer>
          <Tabs onChange={handleTabChange} value={tab}>
            <Tab
              value={tabs.JOURNAL_ENTRY}
              label={t('dailyDataDetails.tabs.journalEntry')}
              component={Link}
              to={`${url}/${tabs.JOURNAL_ENTRY}`}
              replace
              style={{
                borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                textTransform: 'none',
              }}
            />
            <Tab
              value={tabs.SCANS}
              label={t('dailyDataDetails.tabs.scans')}
              component={Link}
              to={`${url}/${tabs.SCANS}`}
              replace
              style={{
                borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                textTransform: 'none',
              }}
            />
            <Tab
              value={tabs.WEARABLE_INFO}
              label={t('dailyDataDetails.tabs.wearableInfo')}
              component={Link}
              to={`${url}/${tabs.WEARABLE_INFO}`}
              replace
              style={{
                borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
                textTransform: 'none',
              }}
            />
          </Tabs>
        </TabContainer>

        <Routes>
          <Route
            path={`${tabs.JOURNAL_ENTRY}/*`}
            element={
              <OvJournalEntryDetailPanel
                journalEntry={selectedDailyData?.journal_entry}
              />
            }
          />
          <Route
            path={`${tabs.SCANS}/*`}
            element={
              <OvUserScanList
                scans={scanList}
                showDetailsAction={false}
                showOperation={false}
              />
            }
          />
          <Route
            index
            element={<Navigate to={`${url}/${tabs.JOURNAL_ENTRY}`} replace />}
          />
          <Route
            path={`${tabs.WEARABLE_INFO}/*`}
            element={
              <OvWearableInfo spikeInfo={selectedDailyData?.spike_info} />
            }
          />
        </Routes>
      </DetailPageWrapper>

      {isLoading && <OvLoadingIndicator position="fixed" />}
    </>
  );
};

export default PatientDailyDataDetails;

const DetailPageWrapper = styled.div`
  margin-left: 1rem;
  box-shadow: ${Variables.boxShadow.defaultBox};
  background-color: ${Colours.WHITE};
  padding: 1.5rem;
  border-radius: ${Variables.borderRadius.SMALL};
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  font-weight: bold;
`;

const PrettyUserName = styled.h6`
  margin: 0;
  font-weight: bold;
  font-size: 1rem;
  line-height: 1.5rem;
`;

const DateInfo = styled.span`
  font-weight: normal;
  font-size: 1rem;
  line-height: 1.5rem;
`;

const Separator = styled.span`
  margin: 0 0.75rem;
`;

const BackCta = styled(OvButton)`
  && {
    padding: 0 0.625rem 0 0.25rem;
    margin-right: 1rem;
    text-transform: none;
    font-weight: bold;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
  }
`;

const StyledArrowBackIcon = styled(ArrowBackIcon)`
  margin-right: 0.25rem;
  height: 1.25rem;
`;

const TabContainer = styled.div`
  margin-bottom: 1.5rem;

  && {
    .MuiTabs-indicator {
      background-color: ${Colours.OV_BASE};
    }
  }
`;

const StyledLink = styled(Link)`
  && {
    text-decoration: none;
    color: ${Colours.OV_BASE};

    &:hover {
      color: ${Colours.OV_BASE_HOVER};
    }
  }
`;

const StyledAlert = styled(Alert)`
  && {
    margin: 0.25rem 0 0.5rem 0;
    border-radius: ${Variables.borderRadius.LARGE};
    background-color: ${Colours.OV_YELLOW};
    color: ${Colours.OV_BASE};

    .MuiAlertTitle-root {
      font-weight: bold !important;
    }
  }
`;
